/**
 * Core Brass API client functions
 */
import { logger } from '../../../utils/logger';
import { RestAPINames } from '../clients/apiConfig';
import * as restClient from '../clients/restClient';
import {
  ActorGroupMember,
  ApiResult,
  GroupType,
  IsMemberOfResponse,
  ListActorGroupsForMemberResponse,
  MembershipResult,
} from './brassApiTypes';

/**
 * API name for Brass API requests
 * Using enum from central configuration for maintainability and testing
 */
const API_NAME = RestAPINames.BRASS_API;

/**
 * Check if a user is a member of a specified team or LDAP group
 *
 * @param username - The username/alias to check (e.g., 'user_alias_1')
 * @param groupId - The group identifier to check membership against. Can be:
 *   - Team ID: Full team identifier (e.g., 'amzn1.abacus.team.xxxxxxxxxxxxxx')
 *   - LDAP Group: Group name (e.g., 'verity-dev')
 * @param groupType - The type of group being checked:
 *   - 'team': For Amazon team IDs (amzn1.abacus.team.*)
 *   - 'ldapGroup': For LDAP group names
 *   Defaults to 'ldapGroup' for backward compatibility
 * @returns Promise resolving to a MembershipResult object containing membership status and error information if applicable
 *
 * @example
 * // Check if user is a member of a team using team ID
 * const teamResult = await isMemberOf('user_alias_1', 'amzn1.abacus.team.xxxxxxxxxxxxxx', 'team');
 * if (teamResult.success && teamResult.isMember) {
 *   console.log('User is a team member');
 * }
 *
 * @example
 * // Check if user is a member of an LDAP group
 * const ldapResult = await isMemberOf('user_alias_1', 'verity-dev', 'ldapGroup');
 * if (ldapResult.success && ldapResult.isMember) {
 *   console.log('User is in LDAP group');
 * }
 *
 * @example
 * // Handle potential errors
 * const { isMember, success, error } = await isMemberOf('johndoe', 'some-group');
 * if (!success) {
 *   // Handle error condition - error object is available
 *   console.error('Membership check failed:', error);
 * } else if (isMember) {
 *   // User is a member, proceed with business logic
 * }
 */
export const isMemberOf = async (
  username: string,
  groupId: string,
  groupType: GroupType = GroupType.LDAP_GROUP
): Promise<MembershipResult> => {
  try {
    const response = await restClient.get<IsMemberOfResponse>({
      apiName: API_NAME,
      path: '/membership/is_member_of',
      queryParams: {
        username,
        groupId,
        groupType,
      },
    });

    return {
      isMember: response.details,
      success: true,
    };
  } catch (error) {
    logger.error('Error checking user membership:', { error, username, groupId, groupType });
    return {
      isMember: false,
      error,
      success: false,
    };
  }
};

/**
 * List all groups that a user is a member of
 *
 * @param username - The username/alias to check (e.g., 'user_alias_1')
 * @param groupType - Type of group to list:
 *   - GroupType.TEAM: List Amazon teams the user belongs to
 *   - GroupType.LDAP_GROUP: List LDAP groups the user belongs to
 * @param nextPage - Optional pagination token for subsequent requests
 * @returns Promise resolving to list of groups with pagination info and error status
 *
 * @example
 * // Get all teams a user belongs to
 * const { data, nextPageToken, success, error } = await listActorGroupsForMember('user_alias_1', GroupType.TEAM);
 * if (success) {
 *   data.forEach(group => {
 *     console.log(`Team: ${group.group_id}`);
 *   });
 * }
 *
 * @example
 * // Get all LDAP groups a user belongs to
 * const { data, nextPageToken, success, error } = await listActorGroupsForMember('user_alias_1', GroupType.LDAP_GROUP);
 * if (success) {
 *   data.forEach(group => {
 *     console.log(`LDAP Group: ${group.group_id}`);
 *   });
 * }
 *
 * @example
 * // Handle errors properly
 * if (!success) {
 *   console.error('Failed to list actor groups:', error);
 *   // Handle the error appropriately
 * }
 *
 * @example
 * // Get next page of results
 * const { data: moreGroups, nextPageToken: newToken } =
 *   await listActorGroupsForMember('user_alias_1', GroupType.TEAM, nextPageToken);
 */
export const listActorGroupsForMember = async (
  username: string,
  groupType: GroupType,
  nextPage?: string
): Promise<ApiResult<ActorGroupMember[]>> => {
  try {
    const queryParams: Record<string, string | number | boolean> = {
      username,
      groupType,
    };

    if (nextPage) {
      queryParams.next_page = nextPage;
    }

    const response = await restClient.get<ListActorGroupsForMemberResponse>({
      apiName: API_NAME,
      path: '/membership/list_actor_groups_for_member',
      queryParams,
    });

    return {
      data: response.details.membershipList,
      nextPageToken: response.details.next_page,
      success: true,
    };
  } catch (error) {
    logger.error('Error listing actor groups for member:', { error, username, groupType });
    return {
      data: [],
      nextPageToken: null,
      error,
      success: false,
    };
  }
};
